import {
  IpmErrorBoundary,
  type IpmMetricTags,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {useMediaQuery} from '@github-ui/use-media-query'
import {Portal} from '@primer/react'
import {clsx} from 'clsx'
import {Profiler, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import type {
  ProductAnnouncementModalEvent,
  ProductAnnouncementModalEventPayload,
  ProductAnnouncementModalProps as ProductAnnouncementModalPropsType,
} from './../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalDesktop from './ProductAnnouncementModalDesktop/ProductAnnouncementModalDesktop'
import ProductAnnouncementModalMobile from './ProductAnnouncementModalMobile/ProductAnnouncementModalMobile'

import Styles from './ProductAnnouncementModal.module.css'

export type {ProductAnnouncementModalProps} from './../ProductAnnouncement/ProductAnnouncement.types'

const defaultProps: Partial<ProductAnnouncementModalPropsType> = {
  shouldShowTabCtas: true,
  shouldShowMainCta: true,
}

function reportModalAction(
  event: ProductAnnouncementModalEvent,
  data: ProductAnnouncementModalEventPayload | undefined,
  tags: IpmMetricTags,
) {
  if (!event.endsWith('_clicked') && event !== 'tab_dropdown_input_copied') return

  reportIpmAction(event.includes('cta') ? 'cta_click' : 'click', {
    ...tags,
    variant: data?.ctaId ?? event,
  })
}

const ProductAnnouncementModal = (props: ProductAnnouncementModalPropsType) => {
  const initializedProps: ProductAnnouncementModalPropsType = {...defaultProps, ...props}
  const {isOpen, title, onClose, onSoftDismiss, onEvent, tabs, analytics, ...propsRest} = initializedProps
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'product-announcement',
      component: 'ProductAnnouncementModal',
      surface: 'modal',
      category: analytics?.category,
      ['nudge_id']: analytics?.category ?? 'product_announcement_modal',
    }),
    [analytics?.category],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    if (isOpen) {
      reportIpmVisible(telemetryTags)
    }
  }, [isOpen, telemetryTags])

  const onCloseWithTelemetry = useCallback(() => {
    reportIpmDismiss(telemetryTags)
    onClose()
  }, [onClose, telemetryTags])

  // Backdrop clicks and the Escape key hide the modal without recording a product dismissal so an
  // accidental click outside doesn't permanently dismiss the announcement. Falls back to the
  // permanent close when no soft-dismiss handler is supplied (e.g. standalone modal usage).
  const onSoftDismissOrClose = useCallback(() => {
    if (onSoftDismiss) {
      reportIpmDismiss(telemetryTags)
      onSoftDismiss()
      return
    }

    onCloseWithTelemetry()
  }, [onCloseWithTelemetry, onSoftDismiss, telemetryTags])

  const [activeTabIndex, setActiveTabIndex] = useState<number>(-1)
  const contentRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const isOpenRef = useRef<boolean>(isOpen)

  const isDesktopView = useMediaQuery('(min-width: 1012px)')

  const onEventWithTelemetry = useCallback(
    (event: ProductAnnouncementModalEvent, data?: ProductAnnouncementModalEventPayload) => {
      reportModalAction(event, data, telemetryTags)
      onEvent?.(event, data)
    },
    [onEvent, telemetryTags],
  )

  const onTabChange = useCallback(
    (tabIndex: number) => {
      const tab = tabs[tabIndex]
      onEventWithTelemetry('tab_clicked', {
        tabIndex,
        ...(tab?.title ? {tabName: tab.title} : {}),
        ...(tab?.analyticsAction ? {analyticsAction: tab.analyticsAction} : {}),
      })

      setActiveTabIndex(tabIndex)
    },
    [onEventWithTelemetry, tabs],
  )

  const onOpenChange = useEffectEvent(() => {
    isOpenRef.current = isOpen

    if (isOpen) {
      // Store previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      setActiveTabIndex(0)

      // Focus on the first tab
      const focusElement =
        contentRef.current?.querySelector<HTMLElement>('[data-tab]') ??
        contentRef.current?.querySelector<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        )
      focusElement?.focus()
      if (!focusElement) contentRef.current?.focus()

      onEventWithTelemetry('opened')
    } else {
      setActiveTabIndex(-1)
      if (activeTabIndex >= 0) onEventWithTelemetry('closed')

      // Restore focus to previously focused element
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onOpenChange()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isOpenRef.current) return

      if (event.key === 'Escape') {
        onSoftDismissOrClose()
        return
      }

      // Focus trap
      if (event.key === 'Tab') {
        const focusableSelector = 'button, [href], [tabindex]:not([tabindex="-1"])'
        const focusableElements = Array.from(
          contentRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || [],
        ).filter(el => !el.hasAttribute('disabled') && el.checkVisibility({checkVisibilityCSS: true}))

        if (!focusableElements.length) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
          }
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onSoftDismissOrClose])

  const modal = (
    <Portal>
      <div
        className={clsx(Styles['ProductAnnouncementModal'], isOpen && Styles['ProductAnnouncementModal--open'])}
        aria-label={title}
        aria-modal={isOpen}
        inert={!isOpen ? true : undefined}
        role="dialog"
        data-testid="product-announcement-modal"
      >
        <div
          role="none"
          className={Styles['ProductAnnouncementModal__backdrop']}
          onClick={onSoftDismissOrClose}
          data-testid="product-announcement-modal-backdrop"
        />

        <div ref={contentRef} tabIndex={-1} className={Styles['ProductAnnouncementModal__content']}>
          {isDesktopView && (
            <ProductAnnouncementModalDesktop
              currentTabIndex={activeTabIndex}
              onTabChange={onTabChange}
              title={title}
              tabs={tabs}
              onClose={onCloseWithTelemetry}
              onEvent={onEventWithTelemetry}
              {...propsRest}
            />
          )}

          {!isDesktopView && (
            <ProductAnnouncementModalMobile
              currentTabIndex={activeTabIndex}
              onTabChange={tabIndex =>
                onTabChange(tabIndex === activeTabIndex ? (tabIndex + 1) % tabs.length : tabIndex)
              }
              title={title}
              tabs={tabs}
              onClose={onCloseWithTelemetry}
              onEvent={onEventWithTelemetry}
              {...propsRest}
            />
          )}
        </div>
      </div>
    </Portal>
  )

  const content = !renderTiming.isEnabled ? (
    modal
  ) : (
    <Profiler id="ProductAnnouncementModal" onRender={renderTiming.onRender}>
      {modal}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="ProductAnnouncementModal">
      {content}
    </IpmErrorBoundary>
  )
}

export default ProductAnnouncementModal
